/**
 * PaddleProvider — loads Paddle.js and initialises the Paddle global.
 *
 * Usage:
 *   1. Wrap the app (or the billing section) with <PaddleProvider>.
 *   2. Call usePaddle() in any child component to access the Paddle global.
 *
 * The Paddle.js client token and environment are fetched from
 * GET /api/billing/config at runtime so they never need to be baked into
 * the build.
 */
import { createContext, useContext, useEffect, useState, ReactNode } from "react";
import { api } from "@/lib/api";

// Minimal type declarations for the Paddle.js global injected by the CDN script.
declare global {
  interface Window {
    Paddle?: PaddleInstance;
  }
}

export interface PaddleCheckoutOptions {
  transactionId?: string;
  // Top-level back-redirect URL. Paddle's subscription checkout docs require
  // the URL to be defined here (`success` argument on Paddle.Checkout.open)
  // for Paddle to auto-redirect back after successful payment.
  success?: string;
  settings?: {
    displayMode?: "overlay" | "inline";
    theme?: "light" | "dark";
    locale?: string;
    successUrl?: string;
  };
}

export interface PaddleInstance {
  Initialize(options: { token: string; environment?: "sandbox" | "production" }): void;
  Checkout: {
    open(options: PaddleCheckoutOptions): void;
    close(): void;
  };
  Update(options: { eventCallback?: (event: PaddleEvent) => void }): void;
}

export interface PaddleEvent {
  name: string;
  data?: {
    id?: string;              // transaction id for checkout events
    transaction_id?: string;  // alternate spelling in some event payloads
    status?: string;
  };
}

interface PaddleContextValue {
  paddle: PaddleInstance | null;
  ready: boolean;
}

const PaddleContext = createContext<PaddleContextValue>({ paddle: null, ready: false });

interface BillingConfig {
  client_token: string;
  environment: "sandbox" | "production";
}

export function PaddleProvider({ children }: { children: ReactNode }) {
  const [paddle, setPaddle] = useState<PaddleInstance | null>(null);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    let cancelled = false;

    async function init() {
      // Fetch client token + environment from the backend
      let config: BillingConfig;
      try {
        config = await api.get<BillingConfig>("/billing/config");
      } catch {
        // Billing not configured — no Paddle.js needed
        return;
      }

      if (!config.client_token || cancelled) return;

      // Inject Paddle.js script if not already present
      const PADDLE_SRC = "https://cdn.paddle.com/paddle/v2/paddle.js";
      const existing = document.querySelector<HTMLScriptElement>(`script[src="${PADDLE_SRC}"]`);

      const scriptReady = () => {
        if (cancelled) return;
        const p = window.Paddle;
        if (!p) return;
        // Paddle.js only accepts 'environment' for sandbox; omit for production.
        if (config.environment === "sandbox") {
          p.Initialize({ token: config.client_token, environment: "sandbox" });
        } else {
          p.Initialize({ token: config.client_token });
        }
        setPaddle(p);
        setReady(true);
      };

      if (existing) {
        // Script already in DOM — may already be loaded
        if (window.Paddle) {
          scriptReady();
        } else {
          existing.addEventListener("load", scriptReady, { once: true });
        }
      } else {
        const script = document.createElement("script");
        script.src = PADDLE_SRC;
        script.async = true;
        script.onload = scriptReady;
        document.head.appendChild(script);
      }
    }

    init();

    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <PaddleContext.Provider value={{ paddle, ready }}>
      {children}
    </PaddleContext.Provider>
  );
}

/** Returns the Paddle instance once ready, or null if Paddle.js is not yet loaded. */
export function usePaddle(): PaddleContextValue {
  return useContext(PaddleContext);
}
